import type {
  CabinetMfaReconfirmResult,
  CabinetMfaRecoveryCodes,
  CabinetMfaSecret,
  CabinetMfaStatus,
  MfaMethod,
} from "@planerius/contracts/cabinet-auth"

import { accessTokenStore } from "./access-token"
import { apiRequest } from "./http"

/**
 * Второй фактор: заведение, выключение, коды восстановления.
 *
 * ДВА ПУТИ К ОДНОМУ И ТОМУ ЖЕ, и различие между ними существенно. Из кабинета человек
 * заводит фактор ПО СВОЕЙ ВОЛЕ, имея сессию (mfaSetup / confirmMfaSetup). С экрана входа
 * он заводит его ПОТОМУ ЧТО ИНАЧЕ НЕ ВОЙДЁТ — сессии у него нет и быть не может, поэтому
 * билетом (enrollMfa / confirmMfaEnrollment). Сервер это различает декораторами; здесь
 * различие видно именами.
 *
 * Всё это работает без выбранного салона и в приостановленном салоне: второй фактор —
 * свойство учётной записи, а не салона.
 */

export function mfaStatus(signal?: AbortSignal): Promise<CabinetMfaStatus> {
  return apiRequest("/cabinet/auth/mfa", { signal })
}

/**
 * Шаг 1 из кабинета: секрет и строка для QR. Секрет приходит РОВНО ОДИН РАЗ.
 *
 * КОД НУЖЕН, КОГДА ФАКТОР УЖЕ ВКЛЮЧЁН: подтверждение нового секрета гасит прежний
 * фактор и все резервные коды, то есть меняет замок. Сервер без кода отказывает
 * (mfa_required), и это не придирчивость: иначе вкладка, оставленная открытой на стойке
 * салона, позволяет кому угодно запереть владельца учётки снаружи.
 */
export function mfaSetup(proof?: { readonly code: string; readonly method: MfaMethod }): Promise<CabinetMfaSecret> {
  return apiRequest("/cabinet/auth/mfa/setup", {
    method: "POST",
    body: proof === undefined ? {} : { code: proof.code, method: proof.method },
  })
}

/** Шаг 2 из кабинета: код из приложения. В ответе — коды восстановления, единственный раз. */
export function confirmMfaSetup(code: string): Promise<CabinetMfaRecoveryCodes> {
  return apiRequest("/cabinet/auth/mfa/setup/confirm", { method: "POST", body: { code } })
}

/** Выключить фактор. Нужен действующий код — из приложения либо резервный. */
export function disableMfa(code: string, method: MfaMethod): Promise<void> {
  return apiRequest("/cabinet/auth/mfa/disable", { method: "POST", body: { code, method } })
}

/** Перевыпустить коды восстановления: прежние гаснут все. */
export function reissueRecoveryCodes(code: string, method: MfaMethod): Promise<CabinetMfaRecoveryCodes> {
  return apiRequest("/cabinet/auth/mfa/recovery-codes", { method: "POST", body: { code, method } })
}

/** Шаг 1 при входе: билетом, без сессии. */
export function enrollMfa(enrollToken: string): Promise<CabinetMfaSecret> {
  return apiRequest("/cabinet/auth/mfa/enroll", { method: "POST", body: { enrollToken } })
}

// Шаг 2 при входе (mfa/enroll/confirm) живёт в auth.api.ts: его ответ несёт выданную
// сессию, а сохранение токена и разбор ответа входа собраны там в одном месте.

/**
 * Повторное подтверждение фактора посреди сеанса — перед выгрузкой, обезличиванием,
 * ключом с ПД. Сервер отдаёт новый access-токен той же сессии со свежей отметкой;
 * он кладётся на место прежнего, и следующий запрос идёт уже подтверждённым.
 */
export async function reconfirmTwoFactor(code: string, method: MfaMethod): Promise<void> {
  const result = await apiRequest<CabinetMfaReconfirmResult>("/cabinet/auth/mfa/reconfirm", {
    method: "POST",
    body: { code, method },
  })
  accessTokenStore.write(result.accessToken, result.expiresAt)
}
